/**
 * Safety-gate approval queue — `GET /safety-gates/approvals/pending`.
 *
 * Migrated onto the CoOrdio list-page pattern (see `ProjectListPage.tsx`, the reference), but this
 * is a work queue rather than a CRUD list, so two things differ from the reference and both are
 * deliberate:
 *
 *  1. The endpoint returns a bare `SafetyGateExecutionDto[]` — no paging, no search, no sort. The
 *     `dataSource` below therefore fetches the whole queue and does the search + slice locally
 *     before handing `createCortexPagedDataSource` a `PagedResult`-shaped object, so the grid's
 *     paging, search and toolbar still work and the offset→page arithmetic still lives in one
 *     tested place. Only `safetyGateId` is a real server-side filter.
 *
 *  2. Approve / Deny are the point of the page, so they stay as row actions. Both go through
 *     `DeleteDialog` (which is not delete-only — it takes `title`, `confirmLabel`, `confirmVariant`
 *     and `busyLabel`), with the comments / reason textarea rendered into its `description` slot.
 *     A missing deny reason is surfaced by throwing from `onConfirm`, which the dialog catches and
 *     renders in its own `delete-dialog-error` slot.
 *
 * The 30-second live refresh is preserved: the interval bumps `listVersion`, which changes the
 * identity of `dataSource`, which is the documented way to make the grid refetch. The grid keeps
 * its own page / search state across that, so a poll does not yank the user back to page 1.
 *
 * ── Column plan ──────────────────────────────────────────────────────────────────────────────────
 *  field           | header      | width | cell type                | filter
 *  ----------------|-------------|-------|--------------------------|-------------------------------
 *  safetyGateName  | Safety Gate |  24%  | text (clickable)         | toolbar search (local)
 *  actionName      | Action      |  20%  | text                     | toolbar search (local)
 *  reason          | Reason      |  22%  | text, maxLength 70 + more| toolbar search (local)
 *  status          | Status      |  10%  | custom → Chip            | —
 *  waitingFor      | Waiting     |  10%  | custom → age since eval  | —
 *  evaluatedAt     | Evaluated   |  14%  | date (locale)            | —
 *
 * `waitingFor` is a synthetic field name, not a DTO property: the grid keys columns by `field`, so
 * a second column over `evaluatedAt` would emit a duplicate React key. It is `type: 'custom'`, so
 * the field path is never read.
 *
 * Dropped: `agentSessionId`, `toolExecutionId`, `safetyGateId`, `approverIds`, `approvedAt`,
 * `approvedBy`, `approvalComments` — all either GUIDs or only meaningful once an execution has left
 * the pending queue.
 *
 * Not available: a real SLA / overdue indicator needs the gate's `autoApprovalTimeoutMinutes`,
 * which lives on `SafetyGateDto` and is not returned by this endpoint. `Waiting` shows elapsed time
 * only — it deliberately does not invent a breach threshold.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  CordioAbpPagedDataTable,
  Chip,
  ShellHeader,
  type ChipColor,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { ShieldCheck, ShieldX } from 'lucide-react';

import { createCortexPagedDataSource } from '../components/data-display/cortexPagedDataSource';
import { DeleteDialog, useDeleteDialog } from '../components/delete-dialog';
import {
  safetyGateService,
  SafetyGateStatus,
  type SafetyGateExecutionDto,
} from '../services/safetyGateService';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';

import type { PagedResult } from '../types/tenant';

import './ApprovalQueuePage.scss';

/** How often the queue reloads itself, in ms. Matches the pre-migration behaviour. */
const POLL_INTERVAL_MS = 30_000;

/** Which confirmation the dialog is currently staged for. */
type QueueAction = 'approve' | 'deny';

/**
 * The staged row plus which action it was staged for.
 *
 * Kept as one object rather than two pieces of state so the dialog copy and the row can never
 * disagree — `useDeleteDialog` is generic precisely so a page can widen the staged item like this.
 */
type StagedApproval = { row: SafetyGateExecutionDto; action: QueueAction };

// A timed-out execution is a terminal non-approval, not an approval; label it accordingly.
const statusLabel = (status: SafetyGateStatus) =>
  status === SafetyGateStatus.Timeout ? 'Timed Out' : status;

const statusChipColor = (status: SafetyGateStatus): ChipColor => {
  switch (status) {
    case SafetyGateStatus.Approved:
      return 'green';
    case SafetyGateStatus.Pending:
      return 'amber';
    case SafetyGateStatus.Denied:
      return 'red';
    case SafetyGateStatus.Timeout:
      return 'blue';
    default:
      return 'default';
  }
};

/** Elapsed time since the gate was evaluated, as the shortest useful unit pair. */
function formatWaitingFor(evaluatedAt: string, now: number): string {
  const evaluated = new Date(evaluatedAt).getTime();
  if (Number.isNaN(evaluated)) return '—';

  const minutes = Math.max(0, Math.floor((now - evaluated) / 60_000));
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;

  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

/** Free-text match across the three fields the row actually shows as text. */
function matchesSearch(row: SafetyGateExecutionDto, term: string): boolean {
  const haystack = [row.safetyGateName, row.actionName, row.reason ?? ''].join(' ').toLowerCase();
  return haystack.includes(term);
}

export const ApprovalQueuePage = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotificationStore();

  /** Bumped by a mutation or by the 30s poll to rebuild `dataSource` and make the grid refetch. */
  const [listVersion, setListVersion] = useState(0);
  /** The comments (approve) or reason (deny) typed into the confirmation dialog. */
  const [note, setNote] = useState('');

  const { dialogRef, pendingItem, openDelete, closeDelete } = useDeleteDialog<StagedApproval>();

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<SafetyGateExecutionDto>(async (query) => {
        const safetyGateId = typeof query.safetyGateId === 'string' ? query.safetyGateId : undefined;
        const all = await safetyGateService.getPendingApprovals(safetyGateId);

        const term = typeof query.searchTerm === 'string' ? query.searchTerm.trim().toLowerCase() : '';
        const matched = term ? all.filter((row) => matchesSearch(row, term)) : all;

        // The endpoint has no paging, so the page window is cut here. `pageNumber` is 1-based —
        // createCortexPagedDataSource already turned the grid's SkipCount into it.
        const start = (query.pageNumber - 1) * query.pageSize;
        const items = matched.slice(start, start + query.pageSize);

        const page: PagedResult<SafetyGateExecutionDto> = {
          items,
          totalCount: matched.length,
          pageNumber: query.pageNumber,
          pageSize: query.pageSize,
          totalPages: Math.max(1, Math.ceil(matched.length / query.pageSize)),
          hasPreviousPage: query.pageNumber > 1,
          hasNextPage: start + query.pageSize < matched.length,
        };
        return page;
      }),
    // `listVersion` is a deliberate dependency: changing the identity of `dataSource` is the
    // documented way to make the grid reload — here for both mutations and the poll below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listVersion],
  );

  useEffect(() => {
    const interval = setInterval(() => setListVersion((v) => v + 1), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<SafetyGateExecutionDto>>>(
    () => [
      { field: 'safetyGateName', header: 'Safety Gate', type: 'text', width: '24%', clickable: true },
      { field: 'actionName', header: 'Action', type: 'text', width: '20%' },
      {
        field: 'reason',
        header: 'Reason',
        type: 'text',
        width: '22%',
        maxLength: 70,
        showReadMore: true,
      },
      {
        field: 'status',
        header: 'Status',
        type: 'custom',
        width: '10%',
        customCell: (row) => (
          <Chip label={statusLabel(row.status)} color={statusChipColor(row.status)} />
        ),
      },
      {
        // Synthetic field — see the column plan above.
        field: 'waitingFor',
        header: 'Waiting',
        type: 'custom',
        width: '10%',
        customCell: (row) => (
          <span className="approval-queue__waiting" title={new Date(row.evaluatedAt).toLocaleString()}>
            {formatWaitingFor(row.evaluatedAt, Date.now())}
          </span>
        ),
      },
      { field: 'evaluatedAt', header: 'Evaluated', type: 'date', width: '14%', dateFormat: 'locale' },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search by gate, action or reason' },
      // No status tabs: this endpoint only ever returns pending executions, so a status tab strip
      // would be three controls that all return the same rows.
      filters: [
        {
          key: 'safetyGateId',
          label: 'Safety Gate Id',
          type: 'text',
          placeholder: 'Gate id',
          description: 'The only filter this endpoint applies server-side.',
        },
      ],
      filterButtonLabel: 'Filter',
    }),
    [],
  );

  /** Stage a row for confirmation, clearing whatever was typed for the previous one. */
  const stage = useCallback(
    (row: SafetyGateExecutionDto, action: QueueAction) => {
      setNote('');
      openDelete({ row, action });
    },
    [openDelete],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<SafetyGateExecutionDto>>>(
    () => [
      {
        icon: 'check',
        tooltip: 'Approve',
        className: 'approval-queue__action--approve',
        // Only a pending execution can still be decided; the rest are terminal.
        visible: (row) => row.status === SafetyGateStatus.Pending,
        onClick: (row) => stage(row, 'approve'),
      },
      {
        icon: 'x',
        tooltip: 'Deny',
        className: 'approval-queue__action--deny',
        visible: (row) => row.status === SafetyGateStatus.Pending,
        onClick: (row) => stage(row, 'deny'),
      },
    ],
    [stage],
  );

  const handleConfirm = useCallback(async () => {
    if (!pendingItem) return;
    const { row, action } = pendingItem;

    if (action === 'approve') {
      await safetyGateService.approveSafetyGate(row.id, { comments: note.trim() || undefined });
      showNotification('Safety gate approved successfully', 'success');
    } else {
      const reason = note.trim();
      // Thrown rather than guarded away: DeleteDialog catches this and renders it in
      // `delete-dialog-error`, which keeps the requirement visible where the user is looking.
      if (!reason) throw new Error('A reason is required to deny a safety gate.');

      await safetyGateService.denySafetyGate(row.id, { reason });
      showNotification('Safety gate denied successfully', 'success');
    }

    setListVersion((v) => v + 1);
  }, [pendingItem, note, showNotification]);

  const isDeny = pendingItem?.action === 'deny';

  return (
    <div className="approval-queue" data-testid="approval-list-page">
      <ShellHeader
        title="Approval Queue"
        description="Safety gates waiting on a human decision before their action is allowed to run."
        data-testid="approval-list-header"
        
      />

      <div data-testid="approval-list-grid">
        <CordioAbpPagedDataTable<SafetyGateExecutionDto>
          className="approval-queue__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          // The gate itself is the only thing to navigate to from an execution row.
          onRowClick={() => navigate(ROUTES.SAFETY_GATES)}
          onCellClick={() => navigate(ROUTES.SAFETY_GATES)}
        />
      </div>

      <DeleteDialog
        ref={dialogRef}
        title={isDeny ? 'Deny safety gate?' : 'Approve safety gate?'}
        entityLabel={
          pendingItem ? `${pendingItem.row.safetyGateName} — ${pendingItem.row.actionName}` : undefined
        }
        dialogIcon={
          isDeny ? (
            <ShieldX className="delete-dialog__icon" />
          ) : (
            <ShieldCheck className="delete-dialog__icon" />
          )
        }
        description={
          <label className="approval-queue__note">
            <span className="approval-queue__note-label">
              {isDeny ? 'Reason (required)' : 'Comments (optional)'}
            </span>
            <textarea
              className="approval-queue__note-input"
              rows={4}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="approval-queue-note"
            />
          </label>
        }
        confirmLabel={isDeny ? 'Deny' : 'Approve'}
        confirmVariant={isDeny ? 'destructive' : 'default'}
        busyLabel={isDeny ? 'Denying…' : 'Approving…'}
        onConfirm={handleConfirm}
        onCancel={closeDelete}
      />
    </div>
  );
};
